import { PanelLeft } from 'lucide-react';
import { useLocation } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@accio3d/design-system/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@accio3d/design-system/breadcrumb';
import { cn } from '@accio3d/design-system/utils';
import arcessioIconSrc from '@accio3d/shared-ui/assets/arcessio-icon.svg';

import { navLabelForPath } from '../../config/nav';
import { useAgentSidebar } from '../../hooks/useAgentSidebar';
import { useAuth } from '../../hooks/useAuth';

interface GlobalTopBarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function GlobalTopBar({ collapsed, onToggleCollapsed }: GlobalTopBarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { open: agentOpen, toggle: toggleAgent } = useAgentSidebar();

  const pageLabel = navLabelForPath(location.pathname);
  const initial = (user?.firstName?.[0] ?? 'G').toUpperCase();

  return (
    <header className="mb-1 flex h-[75px] shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-10">
      <div className="flex items-center gap-[18px]">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          className="flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-primary/[0.06]"
        >
          <PanelLeft className="h-4 w-4" />
        </button>

        <Breadcrumb>
          <BreadcrumbList className="flex-nowrap text-xs">
            <BreadcrumbItem>
              <span className="text-muted-foreground">Workspace</span>
            </BreadcrumbItem>
            {pageLabel && (
              <>
                <BreadcrumbSeparator className="text-border">/</BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-xs font-medium">{pageLabel}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-[18px]">
        <button
          type="button"
          onClick={toggleAgent}
          aria-pressed={agentOpen}
          aria-label={agentOpen ? 'Close the Arcessio assistant' : 'Open the Arcessio assistant'}
          title={`${agentOpen ? 'Close' : 'Open'} the assistant (⌘K)`}
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-2.5 py-[7px] text-xs text-primary transition-colors hover:bg-primary/[0.06]',
            agentOpen && 'bg-primary/[0.06]',
          )}
        >
          <img src={arcessioIconSrc} alt="" className="h-4 w-4 object-contain" />
          Ask Arcessio
        </button>

        <Avatar className="size-8 border border-primary/20 bg-primary/[0.06] text-primary">
          <AvatarImage src={user?.avatarUrl ?? undefined} alt="" />
          <AvatarFallback className="bg-transparent text-xs font-medium">{initial}</AvatarFallback>
        </Avatar>
      </div>
    </header>
  );
}
